/**
 * YouTube Playables — single save blob via ytgame.game.saveData / loadData.
 * Presents a Storage-like API keyed by opaque storage keys (same as localStorage layout).
 */

import { setGameStorageOverride, clearGameStorageOverride } from "./persistence-backend.js";

var SAVE_FORMAT_VERSION = 1;
var flushTimer = null;
var flushDelayMs = 200;
/** @type {(() => Promise<void>) | null} */
var persistFn = null;
/** @type {Record<string, string>} */
var slotMap = {};

function scheduleFlush() {
  if (!persistFn) return;
  if (flushTimer != null) clearTimeout(flushTimer);
  flushTimer = setTimeout(function () {
    flushTimer = null;
    var fn = persistFn;
    if (!fn) return;
    fn().catch(function (e) {
      console.warn("[yt-persistence] save failed:", e);
    });
  }, flushDelayMs);
}

function serializeSnapshot() {
  return JSON.stringify({ v: SAVE_FORMAT_VERSION, slots: slotMap });
}

/**
 * @param {string | null | undefined} raw
 */
export function parseYtSavePayload(raw) {
  if (raw == null || raw === "") {
    slotMap = {};
    return;
  }
  try {
    var parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.slots && typeof parsed.slots === "object") {
      slotMap = {};
      var keys = Object.keys(parsed.slots);
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i];
        var v = parsed.slots[k];
        if (typeof v === "string") slotMap[k] = v;
      }
      return;
    }
  } catch (e) {
    console.warn("[yt-persistence] invalid save payload:", e);
  }
  slotMap = {};
}

/**
 * @param {() => Promise<void>} saveToHost
 * @returns {Storage}
 */
export function createYtGameStorage(saveToHost) {
  persistFn = function () {
    return saveToHost(serializeSnapshot());
  };

  var storage = {
    get length() {
      return Object.keys(slotMap).length;
    },
    clear: function () {
      slotMap = {};
      scheduleFlush();
    },
    getItem: function (key) {
      return Object.prototype.hasOwnProperty.call(slotMap, key) ? slotMap[key] : null;
    },
    setItem: function (key, value) {
      slotMap[key] = String(value);
      scheduleFlush();
    },
    removeItem: function (key) {
      if (Object.prototype.hasOwnProperty.call(slotMap, key)) {
        delete slotMap[key];
        scheduleFlush();
      }
    },
    key: function (index) {
      var keys = Object.keys(slotMap);
      return keys[index] != null ? keys[index] : null;
    },
  };

  setGameStorageOverride(/** @type {Storage} */ (storage));
  return /** @type {Storage} */ (storage);
}

export function teardownYtGameStorage() {
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  persistFn = null;
  slotMap = {};
  clearGameStorageOverride();
}

/**
 * @param {() => Promise<string | null | undefined>} loadFromHost
 */
export async function hydrateYtGameStorage(loadFromHost) {
  var raw = await loadFromHost();
  parseYtSavePayload(typeof raw === "string" ? raw : null);
}

export function flushYtGameStorageNow() {
  if (!persistFn) return Promise.resolve();
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  return persistFn();
}
